import React, { useState, useEffect, useRef } from 'react';
import { Form, theme } from 'antd';
import { observer } from 'mobx-react-lite';
import { EPButton } from '@/components';
import { useStores } from '@/hooks/use-stores';
import { useResponsive } from '@/hooks/useResponsive';
import { UnifiedFormContainer } from './UnifiedSiteSurveyForm.styles';
import HomeContent from '@/comp/Forms/SiteSurveyForm/AccountSelectionStep/HomeContent';
import CustomerInformationContent from '@/comp/Forms/SiteSurveyForm/CustomerInformationStep/CustomerInformationContent';
import SiteConfigurationContent from '@/comp/Forms/SiteSurveyForm/SiteConfigurationStep/SiteConfigurationContent';
import RoomConfigurationContent from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/RoomConfigurationContent';
import ContinueButtonDrawer from './ContinueButtonDrawer';
import { getButtonText, handleFinish, UnifiedSiteSurveyFormProps } from './UnifiedSiteSurveyForm.utils';
import ThankYouScreen from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/ThankYouScreen/ThankYouScreen';
import { FormValues, TIER_VALUES, STATION_NAME_SUFFIXES } from '@/store/SiteSurveyForm/constants';

const { useToken } = theme;

const MemoizedContent = React.memo(({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
});

const UnifiedSiteSurveyForm: React.FC<UnifiedSiteSurveyFormProps> = observer(
  ({
    isLoading,
    isThankYouScreenShown,
    initialAccountId,
    onHomeSubmit,
    onCustomerInformationStepNext,
    onSiteConfigurationStepNext,
    onRoomConfigurationStepFinish,
    form,
    formData = {},
  }) => {
    const { token } = useToken();
    const { siteSurveyStore } = useStores();
    const { isMobile, isTablet } = useResponsive();
    const isDesktop = !isMobile && !isTablet;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCompressorDrawerVisible, setIsCompressorDrawerVisible] = useState(false);

    const hasInitialSyncRef = useRef(false);

    const isCustomerInformationStepOpen = !!siteSurveyStore.expandedSections?.[0];
    const isSiteConfigurationStepOpen = !!siteSurveyStore.expandedSections?.[1];

    useEffect(() => {
      if (siteSurveyStore.currentStep === 1 && !hasInitialSyncRef.current) {
        const formValues: FormValues = {};

        if (initialAccountId) {
          formValues.accountId = initialAccountId;
        }

        if (siteSurveyStore.customerInformationData.tier) {
          formValues.tier = siteSurveyStore.customerInformationData.tier;
        }

        if (siteSurveyStore.customerInformationData.account) {
          formValues.account = siteSurveyStore.customerInformationData.account;
        }

        if (Object.keys(siteSurveyStore.siteConfigurationData).length > 0) {
          formValues.compressorRooms = siteSurveyStore.siteConfigurationData.compressorRooms || 1;
          formValues.entryPoints = siteSurveyStore.siteConfigurationData.entryPoints || 1;
          formValues.systemControllerType = siteSurveyStore.siteConfigurationData.systemControllerType || '';

          const defaultSuffix = formValues.tier === TIER_VALUES.MONITOR_INTERNAL ? STATION_NAME_SUFFIXES.INTERNAL : STATION_NAME_SUFFIXES.MAIN;
          formValues.stationNameSuffix = siteSurveyStore.siteConfigurationData.stationNameSuffix || defaultSuffix;

          formValues.hasMultipleTargetPressures =
            siteSurveyStore.siteConfigurationData.hasMultipleTargetPressures || false;
          formValues.pressureType = siteSurveyStore.siteConfigurationData.pressureType || 'main';

          if (siteSurveyStore.siteConfigurationData.otherCompressorRooms !== undefined) {
            formValues.otherCompressorRooms = siteSurveyStore.siteConfigurationData.otherCompressorRooms;
          }

          if (siteSurveyStore.siteConfigurationData.otherEntryPoints !== undefined) {
            formValues.otherEntryPoints = siteSurveyStore.siteConfigurationData.otherEntryPoints;
          }
        }

        if (Object.keys(siteSurveyStore.roomConfigurationStepData).length > 0) {
          const pressureType = siteSurveyStore.siteConfigurationData.pressureType || 'main';

          if (
            siteSurveyStore.roomConfigurationStepData.rooms &&
            Array.isArray(siteSurveyStore.roomConfigurationStepData.rooms)
          ) {
            formValues[pressureType] = {
              ...formValues[pressureType],
              rooms: siteSurveyStore.roomConfigurationStepData.rooms,
            };
          }

          if (siteSurveyStore.roomConfigurationStepData.compressors) {
            formValues[pressureType] = {
              ...formValues[pressureType],
              compressors: siteSurveyStore.roomConfigurationStepData.compressors,
            };
          }
        }

        form.setFieldsValue(formValues);

        hasInitialSyncRef.current = true;
      }
    }, [
      siteSurveyStore.currentStep,
      initialAccountId,
      form,
      siteSurveyStore.customerInformationData,
      siteSurveyStore.siteConfigurationData,
      siteSurveyStore.roomConfigurationStepData,
    ]);

    const onFinish = async (values: Record<string, unknown>): Promise<void> => {
      try {
        setIsSubmitting(true);

        const currentStep = siteSurveyStore.currentStep;

        if (currentStep === 1) {
          siteSurveyStore.updateCustomerInformationData(values);
        } else if (currentStep === 2) {
          siteSurveyStore.updateSiteConfigurationData(values);
        } else if (currentStep === 3 && !isThankYouScreenShown) {
          siteSurveyStore.updateRoomConfigurationStepData(values);
        }

        await handleFinish(
          values,
          siteSurveyStore.fetchAccountData.bind(siteSurveyStore),
          siteSurveyStore.customerInformationData,
          siteSurveyStore.updateCustomerInformationData.bind(siteSurveyStore),
          siteSurveyStore.updateSiteConfigurationData.bind(siteSurveyStore),
          siteSurveyStore,
          onHomeSubmit,
          onCustomerInformationStepNext,
          onSiteConfigurationStepNext,
          onRoomConfigurationStepFinish,
        );
      } finally {
        setIsSubmitting(false);
      }
    };

    const toggleCustomerInformationStep = (): void => {
      siteSurveyStore.setSectionExpanded(0, !isCustomerInformationStepOpen);
    };

    const toggleSiteConfigurationStep = (): void => {
      siteSurveyStore.setSectionExpanded(1, !isSiteConfigurationStepOpen);
    };

    const handleValidationChange = (pressureType: string, isValid: boolean): void => {
      siteSurveyStore.updateValidation(pressureType, isValid);
    };

    const handleCompressorDrawerVisibilityChange = (isVisible: boolean): void => {
      setIsCompressorDrawerVisible(isVisible);
    };

    const renderCustomerInformationStepContent = (): React.ReactNode => {
      return (
        <MemoizedContent>
          <CustomerInformationContent siteSurveyStore={siteSurveyStore} formId={formData?.formId} form={form} />
        </MemoizedContent>
      );
    };

    const renderContent = (): React.ReactNode => {
      if (isThankYouScreenShown) {
        return <ThankYouScreen />;
      }

      switch (siteSurveyStore.currentStep) {
        case 0:
          return (
            <HomeContent
              isSubmitting={isSubmitting}
              isLoading={isLoading}
              getButtonText={() => getButtonText(siteSurveyStore.currentStep)}
              form={form}
            />
          );
        case 1:
          return <CustomerInformationContent siteSurveyStore={siteSurveyStore} formId={formData?.formId} form={form} />;
        case 2:
          return (
            <SiteConfigurationContent
              siteSurveyStore={siteSurveyStore}
              isCustomerInformationStepOpen={isCustomerInformationStepOpen}
              toggleCustomerInformationStep={toggleCustomerInformationStep}
              renderCustomerInformationStepContent={renderCustomerInformationStepContent}
              form={form}
            />
          );
        case 3:
          return (
            <RoomConfigurationContent
              siteSurveyStore={siteSurveyStore}
              isCustomerInformationStepOpen={isCustomerInformationStepOpen}
              isSiteConfigurationStepOpen={isSiteConfigurationStepOpen}
              toggleCustomerInformationStep={toggleCustomerInformationStep}
              toggleSiteConfigurationStep={toggleSiteConfigurationStep}
              renderCustomerInformationStepContent={renderCustomerInformationStepContent}
              form={form}
              handleValidationChange={handleValidationChange}
              onCompressorDrawerVisibilityChange={handleCompressorDrawerVisibilityChange}
            />
          );
        default:
          return null;
      }
    };

    return (
      <UnifiedFormContainer theme={{ token }}>
        {isThankYouScreenShown ? (
          <ThankYouScreen />
        ) : (
          <>
            <Form
              form={form}
              onFinish={onFinish}
              layout="vertical"
              className={`unified-site-survey-form ${siteSurveyStore.currentStep === 0 ? 'home-step' : 'content-step'}`}
            >
              {renderContent()}

              {siteSurveyStore.currentStep > 0 && isDesktop && (
                <div className="form-actions">
                  <div className="steps-button-container">
                    <EPButton type="primary" loading={isSubmitting || isLoading} htmlType="submit">
                      {getButtonText(siteSurveyStore.currentStep)}
                    </EPButton>
                  </div>
                </div>
              )}

              {siteSurveyStore.currentStep > 0 && !isDesktop && !isCompressorDrawerVisible && (
                <ContinueButtonDrawer
                  visible={true}
                  buttonText={getButtonText(siteSurveyStore.currentStep)}
                  loading={isSubmitting || isLoading}
                  onClick={form.submit}
                  htmlType="button"
                />
              )}
            </Form>
          </>
        )}
      </UnifiedFormContainer>
    );
  },
);

export default UnifiedSiteSurveyForm;
